import * as React from "react";
import { SelectOption } from "@mui/base";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { useQuery } from "react-query";
import { ApiError, SystemSettings, SystemSettingsService } from "gen/clients";

interface Props {
  /**
   * Field name
   */
  name: string;
  onChange: (appBundle: SelectOption<string>) => void;
}

const AppBundleSelect: React.FC<Props> = ({ name, onChange }) => {
  const [, setSearchText] = React.useState<string>();

  const { data, isLoading } = useQuery<SystemSettings, ApiError>(
    ["getSystemSettings"],
    SystemSettingsService.getSystemSettings,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  );

  const sortedOptions = React.useMemo<SelectOption<string>[]>(() => {
    if (!data || !data.appBundles) {
      return [];
    }
    return data.appBundles
      .sort((bundle1, bundle2) => (bundle1.name || bundle1.id).localeCompare(bundle2.name || bundle2.id))
      .map(bundle => ({
        label: bundle.name,
        value: bundle.id
      }));
  }, [data]);

  const options: SelectOption<string>[] = React.useMemo(() => {
    if (!sortedOptions.length) {
      return [];
    }
    return sortedOptions;
  }, [sortedOptions]);

  const onSelect = React.useCallback(
    (selectedOption: SelectOption<string>) => {
      onChange(selectedOption);
    },
    [onChange]
  );

  return (
    <SelectOneAutocompleteField
      name={name}
      options={options}
      onInputTextChange={setSearchText}
      onSelect={onSelect}
      areOptionsLoading={isLoading}
      suppressFilterOptions={true}
      placeholder="Type app bundle name"
      label="App Bundle Name"
      required={true}
    />
  );
};

export { AppBundleSelect };
